import { useMemo } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { renderToString } from 'react-dom/server';
import Handlebars from 'handlebars';
import type { ReactElement } from 'react';

import { invariant, isDefined } from '@e-socna/utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@e-socna/empty';
import type { ResolverEnum } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { getHandlebarsVariables } from '../utils/get-handlebars-variables.util';
import { ResolutionErrorCode } from '../types/resolution-error.type';
import type { ResolutionError } from '../types/resolution-error.type';
import type {
  AnyResolverResult,
  ResolverResult,
} from '../types/resolvers.type';

import { useRequiredResolvers } from './useRequiredResolvers.hook';

const MAX_RESOLUTION_DEPTH = 10;

interface Params {
  readonly template?: string | null | undefined;
  readonly enabled?: boolean;
  readonly maxDepth?: number;
}

interface ContentGeneratorResult extends Omit<ResolverResult<string>, 'error'> {
  readonly error: Error | ResolutionError | null;
  readonly resolutionError?: ResolutionError;
}

/**
 * Recursively collects all resolver variables from a template and its resolved content.
 * Detects cycles by tracking which variables have been seen at each depth.
 */
const collectAllRequiredVariables = (
  template: string,
  allResolvers: Record<ResolverEnum, AnyResolverResult>,
  resolverCommands: readonly string[],
  maxDepth: number,
): Readonly<{
  variables: readonly ResolverEnum[];
  error?: ResolutionError;
  // eslint-disable-next-line max-params
}> => {
  const allVariables = new Set<ResolverEnum>();
  const variablesPerDepth: Set<string>[] = [];

  const collectRecursive = (
    content: string,
    depth: number,
  ): ResolutionError | undefined => {
    if (depth > maxDepth) {
      const remainingVars = getHandlebarsVariables(content);
      return {
        code: ResolutionErrorCode.MaxDepthExceeded,
        message: `Maximum resolution depth (${maxDepth.toFixed()}) exceeded. Remaining variables: ${remainingVars.join(', ')}`,
        depth,
        remainingVariables: remainingVars,
      };
    }

    const variables = getHandlebarsVariables(content);

    if (!variables.length) {
      return undefined;
    }

    // Initialize depth tracking
    variablesPerDepth[depth] = new Set<string>();

    for (const variable of variables) {
      // Validate that the variable is a known resolver
      const isResolver = resolverCommands.includes(variable);
      invariant(isResolver, `Variable "${variable}" is not a known resolver`);

      const resolverEnum = variable as ResolverEnum;

      // Cycle detection: check if this variable appeared in a previous depth
      // and is appearing again (meaning the resolver generates a reference to itself
      // or to a resolver that references it back)
      for (let d = 0; d < depth; d++) {
        if (variablesPerDepth[d]?.has(variable)) {
          // Check if this variable was resolved at depth d but reappears
          // This indicates a cycle
          const cycleVariables = [resolverEnum];
          return {
            code: ResolutionErrorCode.CycleDetected,
            message: `Cycle detected: resolver "${variable}" generates content that references itself (directly or indirectly)`,
            cycleVariables,
          };
        }
      }

      variablesPerDepth[depth].add(variable);
      allVariables.add(resolverEnum);

      // If this resolver is successful, check its content for nested variables
      const resolver = allResolvers[resolverEnum];
      if (resolver.isSuccess && resolver.data) {
        const resolvedContent = renderToString(
          resolver.data.generated as ReactElement,
        );
        const nestedError = collectRecursive(resolvedContent, depth + 1);
        if (nestedError) {
          return nestedError;
        }
      }
    }

    return undefined;
  };

  const error = collectRecursive(template, 0);

  return {
    variables: Array.from(allVariables),
    error,
  };
};

/**
 * Recursively resolves a template by replacing Handlebars variables with their
 * resolved content, then checking if the result contains more variables.
 * Continues until no more variables are found or max depth is reached.
 */
const resolveRecursively = (
  template: string,
  allResolvers: Record<ResolverEnum, AnyResolverResult>,
  resolverCommands: readonly string[],
  maxDepth: number,
): Readonly<{
  content: string;
  error?: ResolutionError;
  // eslint-disable-next-line max-params
}> => {
  const resolvedVariables = new Set<string>();

  const resolveIteration = (
    content: string,
    depth: number,
  ): Readonly<{ content: string; error?: ResolutionError }> => {
    if (depth > maxDepth) {
      const remainingVars = getHandlebarsVariables(content);
      return {
        content,
        error: {
          code: ResolutionErrorCode.MaxDepthExceeded,
          message: `Maximum resolution depth (${maxDepth.toFixed()}) exceeded. Remaining variables: ${remainingVars.join(', ')}`,
          depth,
          remainingVariables: remainingVars,
        },
      };
    }

    const variables = getHandlebarsVariables(content);

    if (!variables.length) {
      return { content };
    }

    // Check for cycles: if we've already resolved these exact variables
    // and they're appearing again, we have a cycle
    const newVariables = variables.filter((v) => !resolvedVariables.has(v));
    const recurringVariables = variables.filter((v) =>
      resolvedVariables.has(v),
    );

    if (recurringVariables.length && !newVariables.length) {
      // All variables have been resolved before but are still present
      // This means a resolver is generating a reference to itself
      return {
        content,
        error: {
          code: ResolutionErrorCode.CycleDetected,
          message: `Cycle detected: resolvers [${recurringVariables.join(', ')}] generate content that references themselves`,
          cycleVariables: recurringVariables as ResolverEnum[],
        },
      };
    }

    // Build the context for Handlebars compilation
    const context = variables.reduce<Record<string, string>>(
      (acc, variable) => {
        const isResolver = resolverCommands.includes(variable);
        invariant(isResolver, `Variable "${variable}" is not a known resolver`);

        const resolverEnum = variable as ResolverEnum;
        const resolver = allResolvers[resolverEnum];

        if (resolver.isSuccess && resolver.data) {
          resolvedVariables.add(variable);
          return {
            ...acc,
            [variable]: renderToString(resolver.data.generated as ReactElement),
          };
        }

        return acc;
      },
      EMPTY_OBJECT,
    );

    // Compile the template with current context
    const compiledContent = Handlebars.compile(content)(context);

    // Check if there are still variables to resolve
    const remainingVariables = getHandlebarsVariables(compiledContent);

    if (!remainingVariables.length) {
      return { content: compiledContent };
    }

    // Recurse to resolve nested variables
    return resolveIteration(compiledContent, depth + 1);
  };

  return resolveIteration(template, 0);
};

export const useContentGenerator = ({
  template,
  enabled: pEnabled,
  maxDepth = MAX_RESOLUTION_DEPTH,
}: Readonly<Params>): ContentGeneratorResult => {
  const enabled =
    (!isDefined(pEnabled) || pEnabled) && typeof template === 'string';

  const { resolvers } = useReferencesContext();

  const resolverCommands = useMemo(
    () => resolvers.map((r) => r.command as string),
    [resolvers],
  );

  const { allResolvers, requiredCommands, setRequiredCommands } =
    useRequiredResolvers({
      enabled,
    });

  // Collect all required variables recursively (including nested ones)
  const { collectedVariables, collectionError } = useMemo(() => {
    if (!enabled) {
      return {
        collectedVariables: EMPTY_ARRAY,
        collectionError: undefined,
      };
    }

    const { variables, error } = collectAllRequiredVariables(
      template,
      allResolvers,
      resolverCommands,
      maxDepth,
    );

    return { collectedVariables: variables, collectionError: error };
  }, [enabled, template, allResolvers, resolverCommands, maxDepth]);

  // Update required commands when collected variables change
  useDeepCompareEffect(() => {
    if (!enabled) {
      return;
    }

    setRequiredCommands(collectedVariables);
  }, [enabled, collectedVariables]);

  if (!enabled) {
    return {
      data: undefined,
      error: null,
      isLoading: false,
      isSuccess: false,
    };
  }

  // Return error if cycle was detected during collection
  if (collectionError) {
    return {
      data: undefined,
      error: collectionError,
      resolutionError: collectionError,
      isLoading: false,
      isSuccess: false,
    };
  }

  const isSuccess =
    !!requiredCommands &&
    requiredCommands.every((command) => allResolvers[command].isSuccess);

  const isLoading =
    !requiredCommands ||
    requiredCommands.some((command) => allResolvers[command].isLoading);

  const requiredCommandFailed = requiredCommands?.find(
    (command) => !!allResolvers[command].error,
  );

  const error = requiredCommandFailed
    ? allResolvers[requiredCommandFailed].error
    : null;

  if (isSuccess) {
    // Perform recursive resolution
    const { content: compiledContent, error: resolutionError } =
      resolveRecursively(template, allResolvers, resolverCommands, maxDepth);

    if (resolutionError) {
      return {
        data: undefined,
        error: resolutionError,
        resolutionError,
        isLoading: false,
        isSuccess: false,
      };
    }

    return {
      data: { generated: compiledContent },
      error,
      isLoading,
      isSuccess,
    };
  }

  return {
    data: undefined,
    error,
    isLoading,
    isSuccess,
  };
};
